import { Component, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CapitalButton } from '@commons/capital-button/capital-button';
import { CapitalInput } from '@commons/capital-input/capital-input';
import { CapitalCard } from '@commons/capital-card/capital-card';
import { PrimaryButton } from '@commons/primary-button/primary-button';
import { CapitalContainer } from '@commons/capital-container/capital-container';
import { CapitalBack } from '@commons/capital-back/capital-back';
import { CapitalSelect } from '@commons/capital-select/capital-select';
import { FormsModule } from '@angular/forms';

interface ComponentExample {
  id: string;
  name: string;
  description: string;
  selector: string;
  examples: {
    title: string;
    description: string;
    code: string;
    props?: any;
  }[];
}

@Component({
  selector: 'app-component-catalog',
  imports: [
    FormsModule,
    CommonModule,
    CapitalButton,
    CapitalInput,
    CapitalCard,
    PrimaryButton,
    CapitalContainer,
    CapitalBack,
    CapitalSelect
  ],
  templateUrl: './component-catalog.html',
  styleUrl: './component-catalog.scss',
})
export class ComponentCatalog {
  selectedComponent = signal<string>('');
  searchTerm = signal<string>('');
  selectModel = '';
  
  sampleSelectOptions = ['Opción 1', 'Opción 2', 'Opción 3'];

  components: ComponentExample[] = [
    {
      id: 'capital-button',
      name: 'Capital Button',
      description: 'Botón personalizado con título, subtítulo e icono',
      selector: 'app-capital-button',
      examples: [
        {
          title: 'Botón básico',
          description: 'Ejemplo con título, subtítulo e icono',
          code: `<app-capital-button 
  title="Mi Cuenta" 
  subtitle="Información personal" 
  icon="/img/account-icon.png">
</app-capital-button>`,
          props: {
            title: 'Mi Cuenta',
            subtitle: 'Información personal',
            icon: '/img/account-icon.png'
          }
        },
        {
          title: 'Botón sin icono',
          description: 'Ejemplo solo con textos',
          code: `<app-capital-button 
  title="Configuración" 
  subtitle="Ajustes de la aplicación">
</app-capital-button>`,
          props: {
            title: 'Configuración',
            subtitle: 'Ajustes de la aplicación',
            icon: ''
          }
        }
      ]
    },
    {
      id: 'capital-input',
      name: 'Capital Input',
      description: 'Input personalizado con placeholder animado y validación',
      selector: 'app-capital-input',
      examples: [
        {
          title: 'Input básico',
          description: 'Input de texto simple',
          code: `<app-capital-input 
  placeholder="Nombre completo" 
  type="text">
</app-capital-input>`,
          props: {
            placeholder: 'Nombre completo',
            type: 'text'
          }
        },
        {
          title: 'Input con formato RUT',
          description: 'Input con formatter para RUT chileno',
          code: `<app-capital-input 
  placeholder="RUT" 
  type="text" 
  formatter="rut">
</app-capital-input>`,
          props: {
            placeholder: 'RUT',
            type: 'text',
            formatter: 'rut'
          }
        },
        {
          title: 'Input con validación personalizada',
          description: 'Input con función de validación',
          code: `<app-capital-input 
  placeholder="Email" 
  type="text" 
  [validator]="emailValidator">
</app-capital-input>`,
          props: {
            placeholder: 'Email',
            type: 'text',
            validator: String.raw`(value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)`
          }
        },
        {
          title: 'Input de contraseña',
          description: 'Input tipo password',
          code: `<app-capital-input 
  placeholder="Contraseña" 
  type="password">
</app-capital-input>`,
          props: {
            placeholder: 'Contraseña',
            type: 'password'
          }
        }
      ]
    },
    {
      id: 'primary-button',
      name: 'Primary Button',
      description: 'Botón principal de la aplicación',
      selector: 'app-primary-button',
      examples: [
        {
          title: 'Botón habilitado',
          description: 'Botón principal activo',
          code: `<app-primary-button 
  text="Continuar">
</app-primary-button>`,
          props: {
            text: 'Continuar',
            disabled: false
          }
        },
        {
          title: 'Botón deshabilitado',
          description: 'Botón principal deshabilitado',
          code: `<app-primary-button 
  text="Procesar" 
  [disabled]="true">
</app-primary-button>`,
          props: {
            text: 'Procesar',
            disabled: true
          }
        }
      ]
    },
    {
      id: 'capital-container',
      name: 'Capital Container',
      description: 'Contenedor responsive con configuraciones de ancho y padding',
      selector: 'app-capital-container',
      examples: [
        {
          title: 'Container por defecto',
          description: 'Container con configuración estándar',
          code: `<app-capital-container>
  <p>Contenido dentro del container</p>
</app-capital-container>`,
          props: {
            disablePaddings: false,
            width: '78%',
            size: 'L',
            compactTop: false
          }
        },
        {
          title: 'Container sin padding',
          description: 'Container con padding deshabilitado',
          code: `<app-capital-container 
  [disablePaddings]="true">
  <p>Contenido sin padding</p>
</app-capital-container>`,
          props: {
            disablePaddings: true,
            width: '78%',
            size: 'L',
            compactTop: false
          }
        },
        {
          title: 'Container ancho personalizado',
          description: 'Container con ancho específico',
          code: `<app-capital-container 
  width="50%" 
  size="M">
  <p>Container más angosto</p>
</app-capital-container>`,
          props: {
            disablePaddings: false,
            width: '50%',
            size: 'M',
            compactTop: false
          }
        },
        {
          title: 'Container compacto arriba',
          description: 'Container con menos espacio superior',
          code: `<app-capital-container 
  [compactTop]="true">
  <p>Contenido con top compacto</p>
</app-capital-container>`,
          props: {
            disablePaddings: false,
            width: '78%',
            size: 'L',
            compactTop: true
          }
        }
      ]
    },
    {
      id: 'capital-card',
      name: 'Capital Card',
      description: 'Tarjeta base para contenido con sombras configurables',
      selector: 'app-capital-card',
      examples: [
        {
          title: 'Card con sombra normal',
          description: 'Tarjeta con sombra estándar',
          code: `<app-capital-card shadow="normal">
  <h3>Título de la tarjeta</h3>
  <p>Contenido de la tarjeta</p>
</app-capital-card>`,
          props: {
            shadow: 'normal'
          }
        },
        {
          title: 'Card con sombra pequeña',
          description: 'Tarjeta con sombra sutil',
          code: `<app-capital-card shadow="small">
  <h3>Título de la tarjeta</h3>
  <p>Contenido de la tarjeta</p>
</app-capital-card>`,
          props: {
            shadow: 'small'
          }
        }
      ]
    },
    {
      id: 'capital-back',
      name: 'Capital Back',
      description: 'Botón para navegar hacia atrás',
      selector: 'app-capital-back',
      examples: [
        {
          title: 'Botón de retroceso',
          description: 'Botón para volver a la página anterior',
          code: `<app-capital-back></app-capital-back>`,
          props: {}
        }
      ]
    },
    {
      id: 'capital-select',
      name: 'Capital Select',
      description: 'Selector desplegable personalizado con placeholder animado',
      selector: 'app-capital-select',
      examples: [
        {
          title: 'Select básico',
          description: 'Selector con opciones simples',
          code: `<app-capital-select 
  placeholder="Selecciona una opción" 
  [options]="['Opción 1', 'Opción 2', 'Opción 3']">
</app-capital-select>`,
          props: {
            placeholder: 'Selecciona una opción',
            options: ['Opción 1', 'Opción 2', 'Opción 3']
          }
        },
        {
          title: 'Select de regiones',
          description: 'Selector con opciones de regiones',
          code: `<app-capital-select 
  placeholder="Región" 
  [options]="['Región Metropolitana', 'Valparaíso', 'Biobío']">
</app-capital-select>`,
          props: {
            placeholder: 'Región',
            options: ['Región Metropolitana', 'Valparaíso', 'Biobío']
          }
        }
      ]
    }
  ];

  get filteredComponents() {
    const term = this.searchTerm().toLowerCase();
    if (!term) return this.components;

    return this.components.filter(component =>
      component.name.toLowerCase().includes(term) ||
      component.description.toLowerCase().includes(term) ||
      component.selector.toLowerCase().includes(term)
    );
  }

  selectComponent(componentId: string) {
    this.selectedComponent.set(componentId);
  }

  getSelectedComponent() {
    return this.components.find(c => c.id === this.selectedComponent());
  }

  copyCode(code: string) {
    navigator.clipboard.writeText(code);
  }

  onSearchChange(event: Event) {
    const target = event.target as HTMLInputElement;
    this.searchTerm.set(target.value);
  }
}